// This is the details and edit panel for one selected client(view/edit one client’s details..
import * as React from 'react';
import Dialog from '@mui/material/Dialog';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import DialogActions from '@mui/material/DialogActions';
import IconButton from '@mui/material/IconButton';
import Box from '@mui/material/Box';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import TextField from '@mui/material/TextField';
import Button from '@mui/material/Button';
import Divider from '@mui/material/Divider';
import Alert from '@mui/material/Alert';
import Autocomplete from '@mui/material/Autocomplete';
import FormControl from '@mui/material/FormControl';
import InputLabel from '@mui/material/InputLabel';
import Select from '@mui/material/Select';
import MenuItem from '@mui/material/MenuItem';
import Tooltip from '@mui/material/Tooltip';
import Chip from '@mui/material/Chip';
import Snackbar from '@mui/material/Snackbar';
import Portal from '@mui/material/Portal';
import Card from '@mui/material/Card';
import CardContent from '@mui/material/CardContent';
import Avatar from '@mui/material/Avatar';
import { alpha, useTheme } from '@mui/material/styles';
import CloseRoundedIcon from '@mui/icons-material/CloseRounded';
import EditRoundedIcon from '@mui/icons-material/EditRounded';
import CheckRoundedIcon from '@mui/icons-material/CheckRounded';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import CloseIcon from '@mui/icons-material/Close';
import DeleteRoundedIcon from '@mui/icons-material/DeleteRounded';
import WarningAmberRoundedIcon from '@mui/icons-material/WarningAmberRounded';
import SwapVertRoundedIcon from '@mui/icons-material/SwapVertRounded';
import AddRoundedIcon from '@mui/icons-material/AddRounded';
import PersonRoundedIcon from '@mui/icons-material/PersonRounded';
import EmailRoundedIcon from '@mui/icons-material/EmailRounded';
import PublicRoundedIcon from '@mui/icons-material/PublicRounded';
import BadgeRoundedIcon from '@mui/icons-material/BadgeRounded';
import PhoneRoundedIcon from '@mui/icons-material/PhoneRounded';
import { deletePhone, swapPhones, addPhone, updatePhone } from '../../../api/clients';
import type { Client } from '../../../api/clients';
import { countries } from 'country-codes-flags-phone-codes';

const REASON_OPTIONS = [
  'Client Instruction',
  'Mobile Verification: Third Party Number',
  'Account Recovery',
  'Data Correction',
  'Security Issue',
  'Phone Number Changed',
  'Lost Access to Previous Contact',
  'System Error',
  'Administrative Update',
  'Compliance Requirement',
  'Development Team Test',
  'Other',
] as const;

const copyToClipboard = async (text: string): Promise<boolean> => {
  if (!text || text === '—') return false;
  try {
    await navigator.clipboard.writeText(text);
    return true;
  } catch {
    return false;
  }
};

function formatStatus(status: string): string {
  return status
    .split('_')
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
    .join(' ');
}

const DEFAULT_COUNTRY_CODE = 'KE';

const SORTED_COUNTRIES = [...countries].sort((a, b) => {
  const nameA = (a.name ?? '').toString();
  const nameB = (b.name ?? '').toString();
  return nameA.localeCompare(nameB);
});

function formatCountryOption(c: (typeof countries)[number]): string {
  const code = c.code ?? '';
  const name = c.name ?? '';
  const dialCode = c.dialCode ?? '';
  // Include ISO code in label so typing "KE" filters naturally.
  return `${c.flag ?? ''} ${code} — ${name}${dialCode ? ` (${dialCode})` : ''}`.trim();
}

const normalizePhoneForParsing = (value: string): string => {
  const trimmed = value.trim();
  if (!trimmed) return '';

  // Keep a leading `+` (if present), then strip everything else to digits.
  if (trimmed.startsWith('+')) return `+${trimmed.slice(1).replace(/[^\d]/g, '')}`;
  return trimmed.replace(/[^\d]/g, '');
};

function parsePhoneToCountryAndSubscriber(phone: string): {
  countryCode: string;
  dialCode: string;
  subscriberDigits: string;
} {
  const normalized = normalizePhoneForParsing(phone);
  const kenya = countries.find((c) => c.code === DEFAULT_COUNTRY_CODE);

  const fallbackDialCode = kenya?.dialCode ?? '+254';
  const fallbackCountryCode = kenya?.code ?? DEFAULT_COUNTRY_CODE;
  if (!normalized) {
    return { countryCode: fallbackCountryCode, dialCode: fallbackDialCode, subscriberDigits: '' };
  }

  // Find the best matching dial code by longest prefix.
  const matches = countries
    .filter((c) => c.dialCode && normalized.startsWith(c.dialCode))
    .sort((a, b) => (b.dialCode.length ?? 0) - (a.dialCode.length ?? 0));

  const best = matches[0];
  if (!best) {
    // If dial code doesn't match (e.g. missing "+"), treat the whole thing as subscriber digits.
    const subscriberDigits = normalized.replace(/\D/g, '');
    return { countryCode: fallbackCountryCode, dialCode: fallbackDialCode, subscriberDigits };
  }

  const subscriberDigits = normalized.slice(best.dialCode.length).replace(/\D/g, '');
  return { countryCode: best.code, dialCode: best.dialCode, subscriberDigits };
}

function composePhoneFromParts(dialCode: string, subscriberDigits: string): {
  fullPhone: string;
  subscriberDigitsAfterStrip: string;
} {
  const cleanDialCode = dialCode?.trim() ?? '';
  const dialDigits = cleanDialCode.replace(/^\+/, '');
  const cleanSubscriber = subscriberDigits.replace(/\D/g, '');

  // If the user accidentally typed/pasted the dial code digits into the
  // subscriber box (e.g. dialCode=+254 and subscriberDigits="2547123..."),
  // strip those digits once to avoid double-prefixing (+254 + 2547...).
  const shouldStrip =
    !!dialDigits &&
    cleanSubscriber.startsWith(dialDigits) &&
    cleanSubscriber.length >= dialDigits.length + 7;

  const subscriberDigitsAfterStrip = shouldStrip
    ? cleanSubscriber.slice(dialDigits.length)
    : cleanSubscriber;

  return {
    fullPhone: `${cleanDialCode}${subscriberDigitsAfterStrip}`,
    subscriberDigitsAfterStrip,
  };
}

function DetailRow({
  label,
  value,
  copyable = false,
  icon,
  accentColor,
  bgColor,
}: {
  label: string;
  value: string;
  copyable?: boolean;
  icon?: React.ReactNode;
  accentColor?: string;
  bgColor?: string;
}) {
  const [copied, setCopied] = React.useState(false);

  const handleCopy = async () => {
    const success = await copyToClipboard(value);
    if (success) {
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    }
  };

  return (
    <Stack direction="row" alignItems="center" spacing={1.5}>
      {icon && (
        <Box
          sx={{
            width: 36,
            height: 36,
            borderRadius: 1.5,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            bgcolor: bgColor ?? 'action.hover',
            color: accentColor ?? 'text.secondary',
            flexShrink: 0,
          }}
        >
          {icon}
        </Box>
      )}
      <Box sx={{ minWidth: 0, flex: 1 }}>
        <Typography
          variant="caption"
          color="text.secondary"
          display="block"
          sx={{ textTransform: 'uppercase', letterSpacing: 0.5, fontWeight: 600 }}
        >
          {label}
        </Typography>
        <Stack direction="row" alignItems="center" spacing={0.75}>
          <Typography variant="body2" fontWeight={500} sx={{ wordBreak: 'break-all' }}>
            {value || '—'}
          </Typography>
          {copyable && value && value !== '—' && (
            <Tooltip title={copied ? 'Copied!' : 'Copy'}>
              <IconButton size="small" onClick={handleCopy} sx={{ p: 0.5 }}>
                {copied ? (
                  <CheckCircleIcon sx={{ fontSize: 13, color: 'success.main' }} />
                ) : (
                  <ContentCopyIcon sx={{ fontSize: 13 }} color="action" />
                )}
              </IconButton>
            </Tooltip>
          )}
        </Stack>
      </Box>
    </Stack>
  );
}

// ─── PhoneEditRow ─────────────────────────────────────────────────────────────

interface PhoneEditRowProps {
  label: string;
  currentValue: string;
  emptyPlaceholder?: string;
  isPrimary?: boolean;
  otherPhone?: string;
  onSave: (newValue: string, reason: string) => Promise<void>;
  onDelete?: (value: string, reason: string, promote?: boolean) => Promise<void>;
}

function PhoneEditRow({
  label,
  currentValue,
  emptyPlaceholder,
  isPrimary,
  otherPhone,
  onSave,
  onDelete,
}: PhoneEditRowProps) {
  const [editOpen, setEditOpen] = React.useState(false);
  const [deleteOpen, setDeleteOpen] = React.useState(false);
  const [selectedCountryCode, setSelectedCountryCode] = React.useState<string>(DEFAULT_COUNTRY_CODE);
  const [subscriberDigits, setSubscriberDigits] = React.useState<string>('');
  const [reason, setReason] = React.useState('');
  const [otherReason, setOtherReason] = React.useState('');
  const [errors, setErrors] = React.useState<{
    newValue?: string;
    reason?: string;
    otherReason?: string;
  }>({});
  const [deleteReason, setDeleteReason] = React.useState('');
  const [deleteOtherReason, setDeleteOtherReason] = React.useState('');
  const [deleteErrors, setDeleteErrors] = React.useState<{
    reason?: string;
    otherReason?: string;
  }>({});
  const [saving, setSaving] = React.useState(false);
  const [copied, setCopied] = React.useState(false);
  const [deleting, setDeleting] = React.useState(false);

  const hasNumber = !!currentValue && currentValue !== '—';
  const visibleValue = hasNumber ? currentValue : emptyPlaceholder || '—';
  const isLastPhone = !otherPhone;
  const showPromoteOption = !!isPrimary && !!otherPhone;

  const selectedCountry = React.useMemo(() => {
    return (
      countries.find((c) => c.code === selectedCountryCode) ??
      countries.find((c) => c.code === DEFAULT_COUNTRY_CODE) ??
      countries[0]
    );
  }, [selectedCountryCode]);

  const { fullPhone: composedPhone, subscriberDigitsAfterStrip } = React.useMemo(() => {
    const dialCode = selectedCountry?.dialCode ?? '+254';
    return composePhoneFromParts(dialCode, subscriberDigits);
  }, [selectedCountry, subscriberDigits]);

  const handleAttemptEdit = () => {
    const parsed = parsePhoneToCountryAndSubscriber(currentValue);
    setSelectedCountryCode(parsed.countryCode);
    setSubscriberDigits(parsed.subscriberDigits);
    setReason('');
    setOtherReason('');
    setErrors({});
    setEditOpen(true);
  };

  const cancelEdit = () => { setEditOpen(false); setErrors({}); };

  const validate = (): boolean => {
    const errs: typeof errors = {};
    if (!subscriberDigitsAfterStrip.trim()) {
      errs.newValue = 'Phone number is required';
    } else if (subscriberDigitsAfterStrip.length < 7) {
      errs.newValue = 'Please enter at least 7 digits';
    }
    if (!reason) {
      errs.reason = 'Reason for change is required';
    } else if (reason === 'Other' && !otherReason.trim()) {
      errs.otherReason = 'Please specify the reason';
    }
    setErrors(errs);
    return Object.keys(errs).length === 0;
  };

  const handleSave = async () => {
    if (!validate()) return;
    setSaving(true);
    try {
      await onSave(composedPhone.trim(), reason === 'Other' ? otherReason.trim() : reason);
      setEditOpen(false);
    } catch {
      // parent already surfaced the error via top-right toast
    } finally {
      setSaving(false);
    }
  };

  const handleCopy = async () => {
    const ok = await copyToClipboard(currentValue);
    if (ok) { setCopied(true); setTimeout(() => setCopied(false), 2000); }
  };

  const handleDelete = async (promote = false) => {
    if (!onDelete || !currentValue) return;
    const errs: typeof deleteErrors = {};
    if (!deleteReason) {
      errs.reason = 'Reason for deletion is required';
    } else if (deleteReason === 'Other' && !deleteOtherReason.trim()) {
      errs.otherReason = 'Please specify the reason';
    }
    if (Object.keys(errs).length > 0) {
      setDeleteErrors(errs);
      return;
    }
    setDeleting(true);
    try {
      await onDelete(
        currentValue,
        deleteReason === 'Other' ? deleteOtherReason.trim() : deleteReason,
        promote,
      );
      setDeleteOpen(false);
    } catch {
      // parent already surfaced the error via top-right toast
    } finally {
      setDeleting(false);
    }
  };

  return (
    <>
      {/* ── Display row ── */}
      <Stack direction="row" alignItems="center" justifyContent="space-between" spacing={1}>
        <Box sx={{ minWidth: 0 }}>
          <Typography
            variant="caption"
            color="text.secondary"
            display="block"
            sx={{ textTransform: 'uppercase', letterSpacing: 0.5, fontWeight: 600 }}
          >
            {label}
          </Typography>
          <Stack direction="row" alignItems="center" spacing={0.75}>
            <Typography variant="body2" fontWeight={500} color={hasNumber ? 'text.primary' : 'text.disabled'}>
              {visibleValue}
            </Typography>
            {hasNumber && (
              <Tooltip title={copied ? 'Copied!' : 'Copy'}>
                <IconButton size="small" onClick={handleCopy} sx={{ p: 0.5 }}>
                  {copied ? (
                    <CheckCircleIcon sx={{ fontSize: 14, color: 'success.main' }} />
                  ) : (
                    <ContentCopyIcon sx={{ fontSize: 14 }} color="action" />
                  )}
                </IconButton>
              </Tooltip>
            )}
          </Stack>
        </Box>

        <Stack direction="row" spacing={0.5}>
          {onDelete && hasNumber && (
            <Tooltip title="Delete phone number">
              <span>
                <IconButton
                  size="small"
                  aria-label={`Delete ${label}`}
                  onClick={() => {
                    setDeleteReason('');
                    setDeleteOtherReason('');
                    setDeleteErrors({});
                    setDeleteOpen(true);
                  }}
                  sx={{ flexShrink: 0, color: 'text.secondary' }}
                >
                  <DeleteRoundedIcon fontSize="small" />
                </IconButton>
              </span>
            </Tooltip>
          )}
          <Tooltip title={hasNumber ? 'Edit number' : 'Add number'}>
            <span>
              <IconButton
                size="small"
                aria-label={`${hasNumber ? 'Edit' : 'Add'} ${label}`}
                onClick={handleAttemptEdit}
                sx={{ flexShrink: 0, color: 'text.secondary' }}
              >
                {hasNumber ? <EditRoundedIcon fontSize="small" /> : <AddRoundedIcon fontSize="small" />}
              </IconButton>
            </span>
          </Tooltip>
        </Stack>
      </Stack>

      {/* ── Edit / Add Dialog ── */}
      <Dialog
        open={editOpen}
        onClose={cancelEdit}
        maxWidth="xs"
        fullWidth
        PaperProps={{ sx: { borderRadius: 2.5 } }}
      >
        <DialogTitle sx={{ pb: 1, borderBottom: '1px solid', borderColor: 'divider' }}>
          <Stack direction="row" spacing={1.5} alignItems="center">
            <Box
              sx={{
                width: 36, height: 36, borderRadius: 1.5, display: 'flex',
                alignItems: 'center', justifyContent: 'center', flexShrink: 0,
                bgcolor: hasNumber ? 'primary.main' : 'success.main', color: '#fff',
              }}
            >
              {hasNumber ? <EditRoundedIcon sx={{ fontSize: 18 }} /> : <AddRoundedIcon sx={{ fontSize: 18 }} />}
            </Box>
            <Box>
              <Typography variant="subtitle1" fontWeight={700} component="div">
                {hasNumber ? `Edit ${label}` : `Add ${label}`}
              </Typography>
              {hasNumber && (
                <Typography variant="caption" color="text.secondary">
                  Current: <strong>{visibleValue}</strong>
                </Typography>
              )}
            </Box>
          </Stack>
        </DialogTitle>

        <DialogContent sx={{ pt: '16px !important' }}>
          <Stack spacing={2}>
            <Stack direction="row" spacing={1} alignItems="flex-start">
              <Autocomplete
                options={SORTED_COUNTRIES}
                value={SORTED_COUNTRIES.find((c) => c.code === selectedCountryCode) ?? undefined}
                onChange={(_, next) => {
                  const nextCode = next?.code ?? DEFAULT_COUNTRY_CODE;
                  setSelectedCountryCode(nextCode);
                  setSubscriberDigits('');
                  if (errors.newValue) setErrors((p) => ({ ...p, newValue: undefined }));
                }}
                autoHighlight
                disableClearable
                sx={{
                  width: 130,
                  flexShrink: 0,
                  '& .MuiAutocomplete-endAdornment': {
                    right: 4,
                  },
                  '& .MuiAutocomplete-popupIndicator': {
                    padding: 0,
                    marginRight: 0,
                    width: 18,
                    height: 18,
                    minWidth: 18,
                    borderRadius: 0.75,
                    color: 'common.white',
                    bgcolor: 'transparent',
                    '&:hover': {
                      bgcolor: 'transparent',
                    },
                  },
                  '& .MuiAutocomplete-popupIndicator .MuiSvgIcon-root': {
                    fontSize: 15,
                  },
                }}
                getOptionLabel={(option) => `${option.flag ?? ''} ${option.dialCode ?? ''}`}
                filterOptions={(options, state) => {
                  const q = state.inputValue.toLowerCase();
                  if (!q) return options;
                  return options.filter((c) => formatCountryOption(c).toLowerCase().includes(q));
                }}
                renderOption={(props, option) => (
                  <Box component="li" {...props} sx={{ gap: 1.25, py: '6px !important', alignItems: 'center' }}>
                    <Typography sx={{ flexShrink: 0, fontSize: '1.1rem', lineHeight: 1 }}>
                      {option.flag}
                    </Typography>
                    <Box sx={{ minWidth: 0 }}>
                      <Typography variant="body2" fontWeight={600} noWrap>
                        {option.code} — {option.name}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        {option.dialCode}
                      </Typography>
                    </Box>
                  </Box>
                )}
                renderInput={(params) => (
                  <TextField {...params} size="small" label="Country" />
                )}
              />

              <TextField
                size="small"
                fullWidth
                label="Number *"
                placeholder="e.g. 712345678"
                value={subscriberDigits}
                autoFocus
                inputProps={{ inputMode: 'numeric', pattern: '[0-9]*' }}
                onChange={(e) => {
                  const digits = e.target.value.replace(/\D/g, '');
                  setSubscriberDigits(digits);
                  if (errors.newValue) setErrors((p) => ({ ...p, newValue: undefined }));
                }}
                error={!!errors.newValue}
                helperText={errors.newValue}
              />
            </Stack>

            <FormControl size="small" fullWidth error={!!errors.reason}>
              <InputLabel id={`${label}-reason-label`}>Reason for change *</InputLabel>
              <Select
                labelId={`${label}-reason-label`}
                value={reason}
                label="Reason for change *"
                onChange={(e) => {
                  setReason(e.target.value);
                  if (e.target.value !== 'Other') setOtherReason('');
                  if (errors.reason) setErrors((p) => ({ ...p, reason: undefined, otherReason: undefined }));
                }}
              >
                {REASON_OPTIONS.map((opt) => (
                  <MenuItem key={opt} value={opt}>{opt}</MenuItem>
                ))}
              </Select>
              {errors.reason && (
                <Typography variant="caption" color="error" sx={{ mt: 0.5, ml: 1.75 }}>
                  {errors.reason}
                </Typography>
              )}
            </FormControl>

            {reason === 'Other' && (
              <TextField
                size="small" fullWidth label="Please specify *"
                placeholder="Enter reason for this change"
                value={otherReason}
                onChange={(e) => {
                  setOtherReason(e.target.value);
                  if (errors.otherReason) setErrors((p) => ({ ...p, otherReason: undefined }));
                }}
                error={!!errors.otherReason}
                helperText={errors.otherReason || 'Please provide details about the reason for this change'}
                multiline rows={2}
              />
            )}
          </Stack>
        </DialogContent>

        <DialogActions sx={{ px: 2.5, pb: 2, pt: 1, borderTop: '1px solid', borderColor: 'divider' }}>
          <Button size="small" variant="outlined" startIcon={<CloseIcon />}
            onClick={cancelEdit} disabled={saving}>
            Cancel
          </Button>
          <Button size="small" variant="contained" startIcon={<CheckRoundedIcon />}
            onClick={handleSave} disabled={saving}>
            {saving ? 'Saving…' : 'Confirm'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* ── Delete Confirmation Dialog ── */}
      <Dialog
        open={deleteOpen}
        onClose={() => !deleting && setDeleteOpen(false)}
        maxWidth="xs"
        fullWidth
        PaperProps={{ sx: { borderRadius: 2.5 } }}
      >
        <DialogTitle sx={{ pb: 1, borderBottom: '1px solid', borderColor: 'divider' }}>
          <Stack direction="row" spacing={1.5} alignItems="center">
            <Box
              sx={{
                width: 36, height: 36, borderRadius: 1.5, display: 'flex',
                alignItems: 'center', justifyContent: 'center', flexShrink: 0,
                bgcolor: 'error.main', color: '#fff',
              }}
            >
              <DeleteRoundedIcon sx={{ fontSize: 18 }} />
            </Box>
            <Typography variant="subtitle1" fontWeight={700} component="div">
              Delete {label}?
            </Typography>
          </Stack>
        </DialogTitle>

        <DialogContent sx={{ pt: '16px !important' }}>
          <Stack spacing={1.5}>
            <Box
              sx={{
                p: 1.5, borderRadius: 1.5,
                bgcolor: (t) => t.palette.mode === 'dark' ? 'rgba(244,67,54,0.08)' : 'error.50',
                border: '1px solid', borderColor: 'error.light',
              }}
            >
              <Typography variant="caption" color="text.secondary" display="block"
                sx={{ textTransform: 'uppercase', letterSpacing: 0.5, fontWeight: 600, mb: 0.25 }}>
                Number to be removed
              </Typography>
              <Typography
                variant="body1"
                fontWeight={700}
                sx={{ color: (t) => (t.palette.mode === 'dark' ? '#8B0000' : t.palette.error.main) }}
              >
                {currentValue}
              </Typography>
            </Box>

            {isLastPhone && (
              <Alert severity="warning" icon={<WarningAmberRoundedIcon fontSize="small" />}>
                This is the <strong>only phone number</strong> on this account. Deleting it will
                leave the client with no phone number.
              </Alert>
            )}

            {showPromoteOption && (
              <Alert severity="info">
                Secondary phone <strong>{otherPhone}</strong> can be promoted to primary at the
                same time.
              </Alert>
            )}

            <FormControl size="small" fullWidth error={!!deleteErrors.reason}>
              <InputLabel id={`${label}-delete-reason-label`}>Reason for deletion *</InputLabel>
              <Select
                labelId={`${label}-delete-reason-label`}
                value={deleteReason}
                label="Reason for deletion *"
                onChange={(e) => {
                  setDeleteReason(e.target.value);
                  if (e.target.value !== 'Other') setDeleteOtherReason('');
                  if (deleteErrors.reason || deleteErrors.otherReason) {
                    setDeleteErrors({ reason: undefined, otherReason: undefined });
                  }
                }}
              >
                {REASON_OPTIONS.map((opt) => (
                  <MenuItem key={opt} value={opt}>{opt}</MenuItem>
                ))}
              </Select>
              {deleteErrors.reason && (
                <Typography variant="caption" color="error" sx={{ mt: 0.5, ml: 1.75 }}>
                  {deleteErrors.reason}
                </Typography>
              )}
            </FormControl>

            {deleteReason === 'Other' && (
              <TextField
                size="small"
                fullWidth
                label="Please specify *"
                placeholder="Enter reason for this deletion"
                value={deleteOtherReason}
                onChange={(e) => {
                  setDeleteOtherReason(e.target.value);
                  if (deleteErrors.otherReason) {
                    setDeleteErrors((prev) => ({ ...prev, otherReason: undefined }));
                  }
                }}
                error={!!deleteErrors.otherReason}
                helperText={
                  deleteErrors.otherReason ||
                  'Please provide details about the reason for this deletion'
                }
                multiline
                rows={2}
              />
            )}
          </Stack>
        </DialogContent>

        <DialogActions sx={{ px: 2.5, pb: 2, pt: 1, borderTop: '1px solid', borderColor: 'divider', gap: 0.5, flexWrap: 'wrap' }}>
          <Button size="small" variant="outlined" startIcon={<CloseIcon />}
            onClick={() => setDeleteOpen(false)} disabled={deleting}>
            Cancel
          </Button>
          {showPromoteOption ? (
            <>
              <Button size="small" variant="outlined" color="error"
                startIcon={<DeleteRoundedIcon />}
                onClick={() => handleDelete(false)} disabled={deleting}>
                {deleting ? 'Deleting…' : 'Delete only'}
              </Button>
              <Button size="small" variant="contained" color="warning"
                startIcon={<DeleteRoundedIcon />}
                onClick={() => handleDelete(true)} disabled={deleting}>
                {deleting ? 'Processing…' : 'Delete & promote'}
              </Button>
            </>
          ) : (
            <Button size="small" variant="contained" color="error"
              startIcon={<DeleteRoundedIcon />}
              onClick={() => handleDelete(false)} disabled={deleting}>
              {deleting ? 'Deleting…' : 'Delete'}
            </Button>
          )}
        </DialogActions>
      </Dialog>
    </>
  );
}

// ─── SwapRow ──────────────────────────────────────────────────────────────────

interface SwapRowProps {
  primaryPhone: string;
  secondaryPhone: string;
  onSwap: (reason: string) => Promise<void>;
}

function SwapRow({ primaryPhone, secondaryPhone, onSwap }: SwapRowProps) {
  const [swapping, setSwapping] = React.useState(false);
  const [confirmOpen, setConfirmOpen] = React.useState(false);
  const [swapReason, setSwapReason] = React.useState('');
  const [swapOtherReason, setSwapOtherReason] = React.useState('');
  const [swapErrors, setSwapErrors] = React.useState<{ reason?: string; otherReason?: string }>({});

  const hasBoth =
    primaryPhone && primaryPhone !== '—' && secondaryPhone && secondaryPhone !== '—';

  const handleSwap = async () => {
    const errs: typeof swapErrors = {};
    if (!swapReason) {
      errs.reason = 'Reason for swap is required';
    } else if (swapReason === 'Other' && !swapOtherReason.trim()) {
      errs.otherReason = 'Please specify the reason';
    }
    if (Object.keys(errs).length > 0) {
      setSwapErrors(errs);
      return;
    }

    setSwapping(true);
    try {
      await onSwap(swapReason === 'Other' ? swapOtherReason.trim() : swapReason);
      setConfirmOpen(false);
    } catch {
      // parent already surfaced the error via top-right toast
    } finally {
      setSwapping(false);
    }
  };

  return (
    <>
      <Tooltip title="Swap primary and secondary phone numbers">
        <span>
          <Button
            size="small"
            variant="outlined"
            startIcon={<SwapVertRoundedIcon />}
            onClick={() => {
              setSwapReason('');
              setSwapOtherReason('');
              setSwapErrors({});
              setConfirmOpen(true);
            }}
          >
            Swap primary & secondary
          </Button>
        </span>
      </Tooltip>

      <Dialog
        open={confirmOpen}
        onClose={() => !swapping && setConfirmOpen(false)}
        maxWidth="xs"
        fullWidth
        PaperProps={{ sx: { borderRadius: 2.5 } }}
      >
        <DialogTitle sx={{ pb: 1, borderBottom: '1px solid', borderColor: 'divider' }}>
          <Stack direction="row" spacing={1.5} alignItems="center">
            <Box
              sx={{
                width: 36, height: 36, borderRadius: 1.5, display: 'flex',
                alignItems: 'center', justifyContent: 'center', flexShrink: 0,
                bgcolor: 'info.main', color: '#fff',
              }}
            >
              <SwapVertRoundedIcon sx={{ fontSize: 18 }} />
            </Box>
            <Typography variant="subtitle1" fontWeight={700} component="div">
              Swap phone numbers?
            </Typography>
          </Stack>
        </DialogTitle>

        <DialogContent sx={{ pt: '16px !important' }}>
          <Stack spacing={1.5}>
            <Stack spacing={1}>
              <Box
                sx={{
                  p: 1.5, borderRadius: 1.5,
                  bgcolor: 'action.hover', border: '1px solid', borderColor: 'divider',
                }}
              >
                <Typography variant="caption" color="text.secondary" display="block"
                  sx={{ textTransform: 'uppercase', letterSpacing: 0.5, fontWeight: 600, mb: 0.25 }}>
                  Primary → will become Secondary
                </Typography>
                <Typography variant="body2" fontWeight={600}>
                  {primaryPhone || '—'}
                </Typography>
              </Box>
              <Box
                sx={{
                  p: 1.5, borderRadius: 1.5,
                  bgcolor: 'action.hover', border: '1px solid', borderColor: 'divider',
                }}
              >
                <Typography variant="caption" color="text.secondary" display="block"
                  sx={{ textTransform: 'uppercase', letterSpacing: 0.5, fontWeight: 600, mb: 0.25 }}>
                  Secondary → will become Primary
                </Typography>
                <Typography variant="body2" fontWeight={600}>
                  {secondaryPhone || '—'}
                </Typography>
              </Box>
            </Stack>

            <TextField
              size="small"
              fullWidth
              select
              label="Reason for swap *"
              value={swapReason}
              onChange={(e) => {
                setSwapReason(e.target.value);
                if (e.target.value !== 'Other') setSwapOtherReason('');
                if (swapErrors.reason || swapErrors.otherReason) {
                  setSwapErrors({ reason: undefined, otherReason: undefined });
                }
              }}
              error={!!swapErrors.reason}
              helperText={swapErrors.reason}
            >
              {REASON_OPTIONS.map((opt) => (
                <MenuItem key={opt} value={opt}>{opt}</MenuItem>
              ))}
            </TextField>

            {swapReason === 'Other' && (
              <TextField
                size="small"
                fullWidth
                label="Please specify *"
                placeholder="Enter reason for this swap"
                value={swapOtherReason}
                onChange={(e) => {
                  setSwapOtherReason(e.target.value);
                  if (swapErrors.otherReason) {
                    setSwapErrors((prev) => ({ ...prev, otherReason: undefined }));
                  }
                }}
                error={!!swapErrors.otherReason}
                helperText={
                  swapErrors.otherReason ||
                  'Please provide details about the reason for this swap'
                }
                multiline
                rows={2}
              />
            )}

            {!hasBoth && (
              <Alert severity="warning" icon={<WarningAmberRoundedIcon fontSize="small" />}>
                One phone number is blank — swapping will move the blank value between fields.
              </Alert>
            )}
          </Stack>
        </DialogContent>

        <DialogActions sx={{ px: 2.5, pb: 2, pt: 1, borderTop: '1px solid', borderColor: 'divider' }}>
          <Button size="small" variant="outlined" startIcon={<CloseIcon />}
            onClick={() => setConfirmOpen(false)} disabled={swapping}>
            Cancel
          </Button>
          <Button size="small" variant="contained" color="info"
            startIcon={<SwapVertRoundedIcon />}
            onClick={handleSwap} disabled={swapping}>
            {swapping ? 'Swapping…' : 'Confirm swap'}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
}

// ─── ClientDetailsDialog ──────────────────────────────────────────────────────

export interface ClientDetailsDialogProps {
  open: boolean;
  client: Client | null;
  onClose: () => void;
  onClientUpdated?: (client: Client) => void;
}

interface ToastState {
  open: boolean;
  severity: 'success' | 'error';
  message: string;
}

export default function ClientDetailsDialog({
  open,
  client,
  onClose,
  onClientUpdated,
}: ClientDetailsDialogProps) {
  const [localClient, setLocalClient] = React.useState<Client | null>(null);
  const [deleteSuccessPopupOpen, setDeleteSuccessPopupOpen] = React.useState(false);
  const [toast, setToast] = React.useState<ToastState>({
    open: false,
    severity: 'success',
    message: '',
  });

  const showToast = (severity: 'success' | 'error', message: string) => {
    setToast({ open: true, severity, message });
  };

  const handleCloseToast = (_: React.SyntheticEvent | Event, reason?: string) => {
    if (reason === 'clickaway') return;
    setToast((prev) => ({ ...prev, open: false }));
  };

  const handleCloseDeleteSuccessPopup = () => {
    setDeleteSuccessPopupOpen(false);
  };

  React.useEffect(() => {
    if (open && client) {
      setLocalClient(client);
    }
  }, [client, open]);

  const handleSavePhone = async (
    field: 'primaryPhone' | 'secondaryPhone',
    newValue: string,
    reason: string,
  ) => {
    if (!localClient) return;
    const fxbold = localClient.fxBold ?? localClient.fxboId;
    if (!fxbold) throw new Error('Missing client ID.');
    const oldValue =
      field === 'primaryPhone'
        ? (localClient.contact?.primaryPhone ?? '')
        : (localClient.contact?.secondaryPhone ?? '');

    const hadExistingValue = !!oldValue?.trim();
    let result;
    try {
      result = hadExistingValue
        ? await updatePhone({ fxbold, oldValue, newValue, reason })
        : await addPhone({
            fxbold,
            contactValue: newValue,
            reason,
            isPrimary: field === 'primaryPhone',
          });
    } catch {
      const msg = 'Network error. Please check your connection and try again.';
      showToast('error', msg);
      throw new Error(msg);
    }

    if (!result.success) {
      const msg =
        result.message ??
        `Failed to ${hadExistingValue ? 'update' : 'add'} ${field === 'primaryPhone' ? 'primary' : 'secondary'} phone.`;
      showToast('error', msg);
      throw new Error(msg);
    }

    const updated: Client = {
      ...localClient,
      contact: { ...localClient.contact, [field]: newValue || undefined },
    };
    setLocalClient(updated);
    onClientUpdated?.(updated);
    showToast(
      'success',
      result.message ??
        `${field === 'primaryPhone' ? 'Primary' : 'Secondary'} phone ${hadExistingValue ? 'updated' : 'added'} successfully.`,
    );
  };

  const handleDeletePhone = async (
    field: 'primaryPhone' | 'secondaryPhone',
    contactValue: string,
    reason: string,
    promote?: boolean,
  ): Promise<void> => {
    if (!localClient) throw new Error('No client loaded.');
    const fxbold = localClient.fxBold ?? localClient.fxboId;
    if (!fxbold) throw new Error('Missing client ID.');

    if (promote && field === 'primaryPhone') {
      let swapResult;
      try {
        swapResult = await swapPhones({ fxbold });
      } catch {
        const msg = 'Network error during promotion. Please check your connection and try again.';
        showToast('error', msg);
        throw new Error(msg);
      }
      if (!swapResult.success) {
        const msg = swapResult.message ?? 'Failed to promote secondary phone to primary.';
        showToast('error', msg);
        throw new Error(msg);
      }
    }

    let result;
    try {
      result = await deletePhone({ fxbold, contactValue, reason });
    } catch {
      const msg = 'Network error. Please check your connection and try again.';
      showToast('error', msg);
      throw new Error(msg);
    }

    if (!result.success) {
      const msg =
        result.message ??
        `Failed to delete ${field === 'primaryPhone' ? 'primary' : 'secondary'} phone.`;
      showToast('error', msg);
      throw new Error(msg);
    }

    const updated: Client = promote && field === 'primaryPhone'
      ? {
          ...localClient,
          contact: {
            ...localClient.contact,
            primaryPhone: localClient.contact?.secondaryPhone,
            secondaryPhone: undefined,
          },
        }
      : {
          ...localClient,
          contact: { ...localClient.contact, [field]: undefined },
        };

    setLocalClient(updated);
    onClientUpdated?.(updated);
    showToast(
      'success',
      promote
        ? 'Primary phone deleted and secondary promoted to primary successfully.'
        : result.message ??
            `${field === 'primaryPhone' ? 'Primary' : 'Secondary'} phone deleted successfully.`,
    );
    setDeleteSuccessPopupOpen(true);
  };

  const handleSwap = async (reason: string): Promise<void> => {
    if (!localClient) throw new Error('No client loaded.');
    const fxbold = localClient.fxBold ?? localClient.fxboId;
    if (!fxbold) throw new Error('Missing client ID.');

    let result;
    try {
      result = await swapPhones({ fxbold, reason });
    } catch {
      const msg = 'Network error. Please check your connection and try again.';
      showToast('error', msg);
      throw new Error(msg);
    }

    if (!result.success) {
      const msg = result.message ?? 'Failed to swap phone numbers.';
      showToast('error', msg);
      throw new Error(msg);
    }

    const updated: Client = {
      ...localClient,
      contact: {
        ...localClient.contact,
        primaryPhone: localClient.contact?.secondaryPhone,
        secondaryPhone: localClient.contact?.primaryPhone,
      },
    };
    setLocalClient(updated);
    onClientUpdated?.(updated);
    showToast('success', result.message ?? 'Phone numbers swapped successfully.');
  };

  if (!localClient) return null;

  const displayName =
    [localClient.firstName, localClient.lastName].filter(Boolean).join(' ') || '—';
  const fxboId = localClient.fxBold ?? localClient.fxboId ?? '—';
  const clientStatus = localClient.status ?? '';
  const isActive = clientStatus === 'ACTIVE';

  return (
    <>
      <ClientDetailsDialogInner
        open={open}
        onClose={onClose}
        localClient={localClient}
        displayName={displayName}
        fxboId={fxboId}
        clientStatus={clientStatus}
        isActive={isActive}
        deleteSuccessPopupOpen={deleteSuccessPopupOpen}
        handleCloseDeleteSuccessPopup={handleCloseDeleteSuccessPopup}
        handleSavePhone={handleSavePhone}
        handleDeletePhone={handleDeletePhone}
        handleSwap={handleSwap}
      />
      <Portal>
      <Snackbar
        open={toast.open}
        autoHideDuration={5000}
        onClose={handleCloseToast}
        anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
        style={{ zIndex: 9999 }}
      >
        <Alert
          onClose={handleCloseToast}
          severity={toast.severity}
          variant="filled"
          sx={(theme) => {
            const isSuccess = toast.severity === 'success';
            const isDark = theme.palette.mode === 'dark';

            const bg = isSuccess
              ? isDark ? 'hsl(122, 39%, 22%)' : 'hsl(122, 39%, 30%)'
              : isDark ? 'hsl(0, 65%, 28%)' : 'hsl(0, 65%, 35%)';

            const borderColor = isSuccess
              ? isDark ? 'hsl(122, 39%, 42%)' : 'hsl(122, 39%, 48%)'
              : isDark ? 'hsl(0, 65%, 48%)' : 'hsl(0, 65%, 52%)';

            return {
              minWidth: 300,
              backgroundColor: `${bg} !important`,
              border: `1px solid ${borderColor} !important`,
              borderRadius: '10px',
              color: '#fff !important',
              boxShadow: theme.shadows[4],
              '& .MuiAlert-icon, & .MuiAlert-action, & .MuiAlert-message': {
                color: '#fff !important',
              },
              '& .MuiAlert-action .MuiIconButton-root': {
                color: 'rgba(255,255,255,0.8) !important',
                backgroundColor: 'transparent !important',
                '&:hover': { backgroundColor: 'transparent !important' },
              },
            };
          }}
        >
          {toast.message}
        </Alert>
      </Snackbar>
      </Portal>
    </>
  );
}

// ── Inner render split out to use hooks (useTheme) cleanly ────────────────────

function ClientDetailsDialogInner({
  open,
  onClose,
  localClient,
  displayName,
  fxboId,
  clientStatus,
  isActive,
  deleteSuccessPopupOpen,
  handleCloseDeleteSuccessPopup,
  handleSavePhone,
  handleDeletePhone,
  handleSwap,
}: {
  open: boolean;
  onClose: () => void;
  localClient: import('../../../api/clients').Client;
  displayName: string;
  fxboId: string;
  clientStatus: string;
  isActive: boolean;
  deleteSuccessPopupOpen: boolean;
  handleCloseDeleteSuccessPopup: () => void;
  handleSavePhone: (
    field: 'primaryPhone' | 'secondaryPhone',
    newValue: string,
    reason: string,
  ) => Promise<void>;
  handleDeletePhone: (
    field: 'primaryPhone' | 'secondaryPhone',
    contactValue: string,
    reason: string,
    promote?: boolean,
  ) => Promise<void>;
  handleSwap: (reason: string) => Promise<void>;
}) {
  const theme = useTheme();

  const colors = {
    primary: { main: theme.palette.primary.main, bg: alpha(theme.palette.primary.main, 0.1) },
    success: { main: theme.palette.success.main, bg: alpha(theme.palette.success.main, 0.1) },
    info:    { main: theme.palette.info.main,    bg: alpha(theme.palette.info.main,    0.1) },
    warning: { main: theme.palette.warning.main, bg: alpha(theme.palette.warning.main, 0.1) },
    error:   { main: theme.palette.error.main,   bg: alpha(theme.palette.error.main,   0.1) },
  };

  const initials = displayName
    .split(' ')
    .filter(Boolean)
    .slice(0, 2)
    .map((n) => n[0]?.toUpperCase() ?? '')
    .join('');

  return (
    <>
      <Dialog
        open={open}
        onClose={onClose}
        maxWidth="sm"
        fullWidth
        PaperProps={{ sx: { borderRadius: 2.5, maxHeight: '92vh' } }}
      >
        {/* ── Dialog Title bar ── */}
        <DialogTitle
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            pr: 1,
            py: 1.5,
            borderBottom: '1px solid',
            borderColor: 'divider',
          }}
        >
          <Stack direction="row" spacing={1} alignItems="center">
            <PersonRoundedIcon color="action" fontSize="small" />
            <Typography variant="subtitle1" fontWeight={700} component="span">
              Client Details
            </Typography>
          </Stack>
          <IconButton aria-label="Close" onClick={onClose} size="small">
            <CloseRoundedIcon />
          </IconButton>
        </DialogTitle>

        <DialogContent sx={{ p: 0 }}>
          <Stack spacing={0}>
            {/* ── Hero / Identity banner ── */}
            <Box
              sx={{
                px: 3,
                py: 2.5,
                background: (t) =>
                  t.palette.mode === 'dark'
                    ? `linear-gradient(135deg, ${alpha(t.palette.primary.main, 0.18)} 0%, ${alpha(t.palette.primary.main, 0.06)} 100%)`
                    : `linear-gradient(135deg, ${alpha(t.palette.primary.main, 0.1)} 0%, ${alpha(t.palette.primary.main, 0.03)} 100%)`,
                borderBottom: '1px solid',
                borderColor: 'divider',
              }}
            >
              <Stack direction="row" spacing={2} alignItems="center">
                <Avatar
                  sx={{
                    width: 52,
                    height: 52,
                    bgcolor: colors.primary.main,
                    fontSize: '1.1rem',
                    fontWeight: 800,
                    flexShrink: 0,
                  }}
                >
                  {initials || '?'}
                </Avatar>
                <Box sx={{ minWidth: 0, flex: 1 }}>
                  <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap">
                    <Typography variant="h6" fontWeight={800} noWrap>
                      {displayName}
                    </Typography>
                    <Chip
                      label={formatStatus(clientStatus || '—')}
                      size="small"
                      color={isActive ? 'success' : 'error'}
                      sx={{ fontWeight: 700, fontSize: '0.68rem', height: 22 }}
                    />
                  </Stack>
                  <Typography variant="body2" color="text.secondary" noWrap>
                    {localClient.contact?.primaryEmail ?? '—'}
                  </Typography>
                </Box>
              </Stack>
            </Box>

            {/* ── Scrollable content ── */}
            <Box sx={{ px: 2.5, py: 2, overflow: 'auto' }}>
              <Stack spacing={2}>

                {/* Personal Info */}
                <Card
                  variant="outlined"
                  sx={{
                    borderLeft: `3px solid ${colors.primary.main}`,
                    transition: 'box-shadow 0.2s',
                    '&:hover': { boxShadow: 2 },
                  }}
                >
                  <CardContent sx={{ pb: '16px !important' }}>
                    <Typography
                      variant="caption"
                      color="text.secondary"
                      sx={{
                        display: 'block',
                        mb: 1.5,
                        textTransform: 'uppercase',
                        letterSpacing: 0.6,
                        fontWeight: 700,
                        color: colors.primary.main,
                      }}
                    >
                      Personal Info
                    </Typography>
                    <Stack spacing={1.75}>
                      <DetailRow
                        label="Full Name"
                        value={displayName}
                        icon={<PersonRoundedIcon sx={{ fontSize: 18 }} />}
                        accentColor={colors.primary.main}
                        bgColor={colors.primary.bg}
                      />
                      <Divider />
                      <DetailRow
                        label="Country"
                        value={localClient.country ?? ''}
                        icon={<PublicRoundedIcon sx={{ fontSize: 18 }} />}
                        accentColor={colors.info.main}
                        bgColor={colors.info.bg}
                      />
                      <Divider />
                      <DetailRow
                        label="FXBO ID"
                        value={fxboId}
                        copyable
                        icon={<BadgeRoundedIcon sx={{ fontSize: 18 }} />}
                        accentColor={colors.warning.main}
                        bgColor={colors.warning.bg}
                      />
                    </Stack>
                  </CardContent>
                </Card>

                {/* Contact Info */}
                <Card
                  variant="outlined"
                  sx={{
                    borderLeft: `3px solid ${colors.info.main}`,
                    transition: 'box-shadow 0.2s',
                    '&:hover': { boxShadow: 2 },
                  }}
                >
                  <CardContent sx={{ pb: '16px !important' }}>
                    <Typography
                      variant="caption"
                      sx={{
                        display: 'block',
                        mb: 1.5,
                        textTransform: 'uppercase',
                        letterSpacing: 0.6,
                        fontWeight: 700,
                        color: colors.info.main,
                      }}
                    >
                      Contact Info
                    </Typography>
                    <DetailRow
                      label="Email"
                      value={localClient.contact?.primaryEmail ?? ''}
                      copyable
                      icon={<EmailRoundedIcon sx={{ fontSize: 18 }} />}
                      accentColor={colors.info.main}
                      bgColor={colors.info.bg}
                    />
                  </CardContent>
                </Card>

                {/* Phone Numbers */}
                <Card
                  variant="outlined"
                  sx={{
                    borderLeft: `3px solid ${colors.warning.main}`,
                    transition: 'box-shadow 0.2s',
                    '&:hover': { boxShadow: 2 },
                  }}
                >
                  <CardContent sx={{ pb: '16px !important' }}>
                    <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 2 }}>
                      <Box
                        sx={{
                          width: 28,
                          height: 28,
                          borderRadius: 1,
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          bgcolor: colors.warning.bg,
                          color: colors.warning.main,
                          flexShrink: 0,
                        }}
                      >
                        <PhoneRoundedIcon sx={{ fontSize: 16 }} />
                      </Box>
                      <Typography
                        variant="caption"
                        sx={{
                          textTransform: 'uppercase',
                          letterSpacing: 0.6,
                          fontWeight: 700,
                          color: colors.warning.main,
                        }}
                      >
                        Phone Numbers
                      </Typography>
                    </Stack>

                    <Stack spacing={2}>
                      <PhoneEditRow
                        label="Primary phone"
                        currentValue={localClient.contact?.primaryPhone ?? ''}
                        isPrimary
                        otherPhone={localClient.contact?.secondaryPhone}
                        onSave={(val, reason) => handleSavePhone('primaryPhone', val, reason)}
                        onDelete={(val, reason, promote) =>
                          handleDeletePhone('primaryPhone', val, reason, promote)
                        }
                      />

                      <Divider />

                      <PhoneEditRow
                        label="Secondary phone"
                        currentValue={localClient.contact?.secondaryPhone ?? ''}
                        emptyPlaceholder="No number added"
                        isPrimary={false}
                        otherPhone={localClient.contact?.primaryPhone}
                        onSave={(val, reason) => handleSavePhone('secondaryPhone', val, reason)}
                        onDelete={(val, reason) => handleDeletePhone('secondaryPhone', val, reason)}
                      />

                      <Divider />
                      <SwapRow
                        primaryPhone={localClient.contact?.primaryPhone ?? ''}
                        secondaryPhone={localClient.contact?.secondaryPhone ?? ''}
                        onSwap={handleSwap}
                      />
                    </Stack>
                  </CardContent>
                </Card>

              </Stack>
            </Box>
          </Stack>
        </DialogContent>

        <DialogActions
          sx={{
            px: 2.5,
            py: 1.5,
            borderTop: '1px solid',
            borderColor: 'divider',
          }}
        >
          <Button onClick={onClose} variant="outlined" size="small">
            Close
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={deleteSuccessPopupOpen}
        onClose={handleCloseDeleteSuccessPopup}
        maxWidth="xs"
        fullWidth
        PaperProps={{ sx: { borderRadius: 2.5 } }}
      >
        <DialogTitle sx={{ pb: 1, borderBottom: '1px solid', borderColor: 'divider' }}>
          <Stack direction="row" spacing={1.5} alignItems="center">
            <Box
              sx={{
                width: 36, height: 36, borderRadius: 1.5, display: 'flex',
                alignItems: 'center', justifyContent: 'center', flexShrink: 0,
                bgcolor: 'success.main', color: '#fff',
              }}
            >
              <CheckCircleIcon sx={{ fontSize: 20 }} />
            </Box>
            <Typography variant="subtitle1" fontWeight={700} component="div">
              Deletion Successful
            </Typography>
          </Stack>
        </DialogTitle>
        <DialogContent sx={{ pt: '16px !important' }}>
          <Stack spacing={1.5}>
            <Typography variant="body2" color="text.secondary">
              The phone number has been removed from this client's profile in the CSP.
            </Typography>
            <Alert
              severity="warning"
              icon={<WarningAmberRoundedIcon fontSize="small" />}
              sx={{ borderRadius: 1.5 }}
            >
              <Typography variant="body2" fontWeight={600}>
                Action required in FXBO
              </Typography>
              <Typography variant="caption" color="text.secondary">
                Please proceed to FXBO and complete the deletion there to keep records in sync.
              </Typography>
            </Alert>
          </Stack>
        </DialogContent>
        <DialogActions sx={{ px: 2.5, pb: 2, pt: 1, borderTop: '1px solid', borderColor: 'divider' }}>
          <Button variant="contained" color="success" onClick={handleCloseDeleteSuccessPopup} size="small">
            Got it, I'll update FXBO
          </Button>
        </DialogActions>
      </Dialog>

    </>
  );
}
